import type { Cents } from "./money";
import type { FinancialBucket } from "./buckets";
import type { CostType } from "./proposedCostLine";

/**
 * Actual Expense — a manually entered record of money actually spent.
 *
 * Manual expenses are a PERMANENT source of Actual Cost (approved Phase 2
 * design; master spec Section 33). The richer Purchase → Purchase Line →
 * Allocation architecture arrives in a later phase as an ADDITIONAL source;
 * these records are never converted away or reinterpreted.
 *
 * Money: `amountCents` is the pre-tax amount and `taxCents` the actual
 * sales tax, stored separately per spec Section 25. The expense total is
 * amount + tax, and totals always include tax.
 *
 * Classification: the category supplies a default Financial Bucket;
 * `bucketOverride` lets this specific expense belong to a different stage
 * of project economics (e.g. Landscaping defaulting to Holding, overridden
 * to Renovation for "new sod and flower beds").
 *
 * Linking (spec Section 31, three levels): an expense may DIRECTLY link to
 * one Proposed Cost Line (precise) OR one Project Feature (broader) —
 * never both — or to neither (project-level). An expense linked to a line
 * that belongs to a feature contributes to that feature's actual total
 * implicitly; because the two direct links are mutually exclusive, every
 * expense is counted exactly once.
 *
 * History: expenses are voided, never physically deleted (spec Section 11).
 * Voided expenses are excluded from every total but remain on record.
 */
export interface ActualExpense {
  id: string;
  projectId: string;
  /** ISO date of the expense, e.g. "2026-08-24". */
  date: string;
  description: string;
  vendor: string;
  categoryId: string;
  /** Pre-tax amount, integer cents. */
  amountCents: Cents;
  /** Actual sales tax paid, integer cents (0 when none). */
  taxCents: Cents;
  bucketOverride: FinancialBucket | null;
  linkedProposedLineId: string | null;
  /** Direct feature link; mutually exclusive with linkedProposedLineId. */
  linkedFeatureId: string | null;
  /**
   * Cost BASIS (Phase 9C, spec §29A): material / labor / other, so this
   * money reconciles against the comparable proposed basis. NULL is
   * legitimate — a lump-sum expense covering mixed scope — and is reported
   * as "not comparable" rather than being guessed into a basis.
   */
  costType: CostType | null;
  notes: string;
  voidedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ActualExpenseInput {
  date: string;
  description: string;
  vendor: string;
  categoryId: string;
  amountCents: Cents;
  taxCents: Cents;
  bucketOverride: FinancialBucket | null;
  linkedProposedLineId: string | null;
  linkedFeatureId?: string | null;
  /** Cost basis (Phase 9C). Null = mixed/unclassified; never guessed. */
  costType?: CostType | null;
  notes: string;
}

export function validateActualExpenseInput(input: ActualExpenseInput): string[] {
  const problems: string[] = [];
  if (input.date.trim() === "") {
    problems.push("Date is required.");
  }
  if (input.description.trim() === "") {
    problems.push("Description is required.");
  }
  if (input.categoryId.trim() === "") {
    problems.push("Category is required.");
  }
  if (!Number.isSafeInteger(input.amountCents)) {
    problems.push("Amount must be a valid dollar amount.");
  }
  if (!Number.isSafeInteger(input.taxCents) || input.taxCents < 0) {
    problems.push("Sales tax must be a valid dollar amount (0 or more).");
  }
  if (input.linkedProposedLineId != null && input.linkedFeatureId != null) {
    problems.push(
      "An expense can link to a proposed item OR a feature, not both.",
    );
  }
  return problems;
}
